import type { DynamicTableColumnConfig, DynamicTableConfig, Question } from '../../config/types';
import type { DynamicTableExcelOptions } from '../dynamic-table-excel';
import type { ListPageExcelOptions } from './editor-model';

const CONTINUOUS_ROW_FIELD = /^continuousRow(\d+)(.+)$/;
const LIST_ROW_FIELD = /^(\d+)-(.+)$/;

const isExportedColumn = (column: DynamicTableColumnConfig) =>
  column.enabled !== false && !column.calculation;

const getColumnRules = (column: DynamicTableColumnConfig) => ({
  required: column.required === true,
  ...(column.allowNegativeNumbers === false ? { min: 0 } : {}),
});

const getNumberQuestion = (
  column: DynamicTableColumnConfig,
  name = column.key,
  title = column.label,
): Question => ({
  name,
  title,
  questionType: column.allowDecimal ? 'textInput' : 'instanceInput',
  hideOptionalLabel: true,
  rules: {
    ...getColumnRules(column),
    ...(column.allowDecimal ? { number: true, float: true } : {}),
  },
});

const getColumnQuestions = (column: DynamicTableColumnConfig): Question[] => {
  if (column.inputType === 'select') {
    return [
      {
        name: column.key,
        title: column.label,
        questionType: 'select',
        options: column.options ?? [],
        hideOptionalLabel: true,
        rules: getColumnRules(column),
      },
    ];
  }

  if (column.inputType === 'text') {
    return [
      {
        name: column.key,
        title: column.label,
        questionType: 'textInput',
        hideOptionalLabel: true,
        rules: getColumnRules(column),
      },
    ];
  }

  if (column.inputType === 'duration' && column.duration) {
    return [
      getNumberQuestion(column, column.key, `${column.label} - number`),
      {
        name: column.duration.unitKey,
        title: `${column.label} - unit`,
        questionType: 'select',
        options: column.duration.units.map(({ value }) => value),
        hideOptionalLabel: true,
        rules: getColumnRules(column),
      },
    ];
  }

  return [getNumberQuestion(column)];
};

export const getDynamicTableListPageExcelQuestions = (
  options: DynamicTableExcelOptions,
): Question[] => options.columns.filter(isExportedColumn).flatMap(getColumnQuestions);

const getListPageAnswers = (
  options: DynamicTableExcelOptions,
  questions: Question[],
): Record<string, unknown> => {
  const fieldNames = new Set(questions.map(({ name }) => String(name)));
  const answers: Record<string, unknown> = {};

  Object.entries(options.values).forEach(([fieldName, value]) => {
    const match = fieldName.match(CONTINUOUS_ROW_FIELD);
    if (!match || !fieldNames.has(match[2]) || value === '') return;
    answers[`${match[1]}-${match[2]}`] = value;
  });

  options.columns.filter(isExportedColumn).forEach((column) => {
    if (column.inputType !== 'duration' || !column.duration?.defaultUnit) return;
    const { defaultUnit, unitKey } = column.duration;
    Object.keys(answers).forEach((answerName) => {
      const match = answerName.match(LIST_ROW_FIELD);
      if (!match || match[2] !== column.key) return;
      const unitAnswerName = `${match[1]}-${unitKey}`;
      if (answers[unitAnswerName] === undefined) answers[unitAnswerName] = defaultUnit;
    });
  });

  return answers;
};

export const getDynamicTableListPageExcelOptions = (
  options: DynamicTableExcelOptions,
  context: Pick<ListPageExcelOptions, 'tenant' | 'pageName'>,
): ListPageExcelOptions => {
  const questions = getDynamicTableListPageExcelQuestions(options);
  return {
    title: options.title,
    pageName: context.pageName,
    questions,
    answers: getListPageAnswers(options, questions),
    tenant: context.tenant,
  };
};

export const convertListPageAnswersToDynamicTableValues = (
  config: DynamicTableConfig,
  answers: Record<string, unknown>,
): Record<string, string> => {
  const columns = config.columns.filter(isExportedColumn);
  const fieldNames = new Set(
    columns.flatMap((column) => [
      column.key,
      ...(column.inputType === 'duration' && column.duration ? [column.duration.unitKey] : []),
    ]),
  );
  const values: Record<string, string> = {};

  Object.entries(answers).forEach(([answerName, value]) => {
    const match = answerName.match(LIST_ROW_FIELD);
    if (!match || !fieldNames.has(match[2])) return;
    const text = String(value ?? '').trim();
    if (text) values[`continuousRow${match[1]}${match[2]}`] = text;
  });

  columns.forEach((column) => {
    if (column.inputType !== 'duration' || !column.duration?.defaultUnit) return;
    const { defaultUnit, unitKey } = column.duration;
    Object.keys(values).forEach((fieldName) => {
      const match = fieldName.match(CONTINUOUS_ROW_FIELD);
      if (!match || match[2] !== column.key) return;
      const unitFieldName = `continuousRow${match[1]}${unitKey}`;
      if (values[unitFieldName] === undefined) values[unitFieldName] = defaultUnit;
    });
  });

  return values;
};
